<template>
  <div class="panel panel-default bf-messenger">
    <div class="panel-heading bf-message-header">
      <button
        class="button-unstyled bf-back"
        title="Back to Direct Conversations"
        @click="goBack"
      >
        <FAIcon icon="arrow-left" />
      </button>

      <UserAvatar
        v-if="primaryRecipient"
        :user="primaryRecipient"
        :compact="true"
        class="bf-header-avatar"
      />

      <div class="bf-header-identity">
        <strong>{{ conversationName }}</strong>

        <span
          v-if="recipientHandle"
          class="bf-header-handle"
        >
          {{ recipientHandle }}
        </span>

        <span
          v-else-if="recipients.length > 1"
          class="bf-header-handle"
        >
          {{ recipients.length }} participants
        </span>
      </div>

      <button
        v-if="conversation"
        class="button-unstyled bf-members-button"
        title="Conversation participants"
        @click="$router.push({
          name: 'dm-conversation-recipients',
          params: { id: conversationId }
        })"
      >
        <FAIcon icon="users" />
      </button>
    </div>

    <div
      ref="messageList"
      class="bf-message-list"
    >
      <div
        v-if="messages.length === 0"
        class="bf-empty-conversation"
      >
        No messages in this conversation yet.
      </div>

      <template
        v-for="(status, index) in messages"
        :key="status.id"
      >
        <div
          v-if="!sameDayAsPrevious(index)"
          class="bf-date-divider"
        >
          <span>{{ messageDate(status) }}</span>
        </div>

        <div
          class="bf-message-row"
          :class="{ mine: isMine(status) }"
        >
          <UserAvatar
            v-if="!isMine(status) && status.user"
            :user="status.user"
            :compact="true"
            class="bf-message-avatar"
          />

          <div class="bf-message-column">
            <div class="bf-message-sender">
              {{ senderName(status) }}
            </div>

            <div class="bf-message-bubble">
              <div
                v-if="messageText(status)"
                class="bf-message-text"
              >
                {{ messageText(status) }}
              </div>

              <div
                v-if="status.attachments && status.attachments.length"
                class="bf-message-attachments"
              >
                <Attachment
                  v-for="attachment in status.attachments"
                  :key="attachment.id"
                  :attachment="attachment"
                  :compact="true"
                />
              </div>
            </div>

            <div class="bf-message-time">
              {{ messageTime(status) }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="conversation && recipients.length"
      class="bf-message-composer"
    >
      <PostStatusForm
        status-scope="direct"
        :attentions="composerAttentions"
        :post-handler="postDMStatus"
        :disable-subject="true"
        :disable-scope-selector="true"
        :disable-visibility-selector="true"
        :disable-notice="true"
        :disable-lock-warning="true"
        :disable-polls="true"
        :disable-sensitivity-checkbox="true"
        :disable-preview="true"
        :submit-on-enter="true"
        submit-label="Send"
        :hide-character-counter="true"
        :preserve-focus="true"
        :auto-focus="true"
        :placeholder="composerPlaceholder"
        @posted="handlePosted"
      />
    </div>
  </div>
</template>

<script src="./dm_conv_timeline.js"></script>

<style lang="scss">
@import '../../_variables.scss';

.bf-messenger {
  display: flex;
  flex-direction: column;
  height: min(78vh, 820px);
  min-height: 560px;
  overflow: hidden;

  .bf-message-header {
    flex: 0 0 auto;
    min-height: 64px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
  }

  .bf-back,
  .bf-members-button {
    flex: 0 0 auto;
    padding: 8px 10px;
    font-size: 1.05em;
  }

  .bf-header-avatar {
    flex: 0 0 auto;

    &.Avatar {
      width: 40px;
      height: 40px;
    }
  }

  .bf-header-identity {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;

    strong {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .bf-header-handle {
    color: var(--faint);
    font-size: 0.88em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .bf-message-list {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 18px 18px 22px;
    scroll-behavior: smooth;
  }

  .bf-empty-conversation {
    text-align: center;
    color: var(--faint);
    padding: 40px 20px;
  }

  .bf-date-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 18px 0;

    span {
      padding: 4px 10px;
      border-radius: 999px;
      background: var(--btn);
      color: var(--faint);
      font-size: 0.8em;
    }
  }

  .bf-message-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin: 8px 0;
    padding-right: 18%;

    &.mine {
      justify-content: flex-end;
      padding-right: 0;
      padding-left: 18%;

      .bf-message-column {
        align-items: flex-end;
      }

      .bf-message-bubble {
        background: var(--link);
        color: var(--linkContrast, #fff);
        border-bottom-right-radius: 4px;
      }

      .bf-message-sender {
        text-align: right;
      }
    }
  }

  .bf-message-avatar {
    flex: 0 0 auto;
    margin-bottom: 18px;

    &.Avatar {
      width: 34px;
      height: 34px;
    }
  }

  .bf-message-column {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    max-width: 100%;
  }

  .bf-message-sender {
    margin: 0 8px 3px;
    color: var(--faint);
    font-size: 0.78em;
  }

  .bf-message-bubble {
    max-width: 100%;
    padding: 9px 12px;
    border-radius: 15px;
    border-bottom-left-radius: 4px;
    background: var(--btn);
    color: var(--text);
    overflow-wrap: anywhere;
  }

  .bf-message-text {
    white-space: pre-wrap;
    line-height: 1.35;
  }

  .bf-message-time {
    margin: 3px 8px 0;
    color: var(--faint);
    font-size: 0.72em;
  }

  .bf-message-attachments {
    margin-top: 6px;
  }

  .bf-message-composer {
    flex: 0 0 auto;
    padding: 8px 10px 10px;
    border-top: 1px solid var(--border, $fallback--border);
    background: var(--bg, $fallback--bg);

    .post-status-form {
      margin: 0;
    }

    .visibility-notice,
    .visibility-tray,
    .spoiler-input,
    .poll-form {
      display: none !important;
    }
  }

  @media (max-width: 600px) {
    height: calc(100vh - 105px);
    min-height: 420px;
    border-radius: 0;

    .bf-message-header {
      min-height: 56px;
      padding: 7px 8px;
    }

    .bf-message-list {
      padding: 12px 9px 18px;
    }

    .bf-message-row {
      padding-right: 8%;

      &.mine {
        padding-left: 8%;
      }
    }

    .bf-message-composer {
      padding: 6px;
    }
  }
}
</style>
